/**
 * aramLayout — Murder Bridge structure blueprints.
 * Positions from aramScale; spacing mirrors HA/Proving Grounds chain.
 *
 * From mid → base: OUTER → INHIB_TURRET → INHIB → 2× NEXUS_TURRET → NEXUS → spawn
 * Lane along +Z: blue +Z (BL) → red −Z (TR).
 */

import {
  ARAM_HALF_LENGTH,
  lolToSandbox
} from './aramScale.js';

/** attackRange in LoL units — live combat uses range/100 (same as champions). */
export const ARAM_STRUCTURE_TIERS = {
  outer: {
    hp: 2550, ad: 152, armor: 40, mr: 40,
    attackRange: 775,
    radius: 35
  },
  inhibitorTurret: {
    hp: 2550, ad: 190, armor: 40, mr: 40,
    attackRange: 775,
    radius: 35
  },
  inhibitor: {
    hp: 4000, ad: 0, armor: 20, mr: 0,
    attackRange: 0, radius: 40, respawnTimer: 300
  },
  nexusTurret: {
    hp: 2550, ad: 190, armor: 40, mr: 40,
    attackRange: 775,
    radius: 35
  },
  nexus: {
    hp: 5500, ad: 152, armor: 15, mr: 0,
    attackRange: 775, radius: 55
  }
};

/**
 * Distance from mid as fraction of half-length (spawn ≈ 0.95).
 * Tuned so mid fight zone is large; base cluster packs near fountain.
 */
export const ARAM_STRUCTURE_ALONG_FRAC = {
  outer: 0.40,
  inhibTurret: 0.62,
  inhib: 0.70,
  nexusTurret: 0.84,
  nexus: 0.91,
  spawn: 0.95
};

/** Absolute along-distance from mid for blue (+Z). */
export const ARAM_STRUCTURE_ALONG = Object.fromEntries(
  Object.entries(ARAM_STRUCTURE_ALONG_FRAC).map(([k, frac]) => [
    k,
    ARAM_HALF_LENGTH * frac
  ])
);

/** Half spacing of paired nexus turrets (LoL ≈ ±220u → sandbox). */
const NT_SIDE = lolToSandbox(220);

function blueprint(id, team, tier, x, z, requires = null) {
  const stats = ARAM_STRUCTURE_TIERS[tier];
  return {
    id,
    team,
    type: tier === 'inhibitor' ? 'inhibitor' : tier === 'nexus' ? 'nexus' : 'turret',
    tier,
    x,
    z,
    requires,
    maxHp: stats.hp,
    ad: stats.ad,
    armor: stats.armor,
    mr: stats.mr,
    attackRange: stats.attackRange,
    radius: stats.radius,
    ...(stats.respawnTimer ? { respawnTimer: stats.respawnTimer } : {})
  };
}

function onLane(along) {
  return { x: 0, z: along };
}

function sideOffset(along, side) {
  return { x: side * NT_SIDE, z: along };
}

function teamStructures(team) {
  const sign = team === 'blue' ? 1 : -1;
  const A = ARAM_STRUCTURE_ALONG;
  const along = (key) => sign * A[key];

  const outerPos = onLane(along('outer'));
  const itPos = onLane(along('inhibTurret'));
  const ihPos = onLane(along('inhib'));
  const nxPos = onLane(along('nexus'));
  const ntLPos = sideOffset(along('nexusTurret'), 1);
  const ntRPos = sideOffset(along('nexusTurret'), -1);

  const outer = blueprint(`${team}-outer`, team, 'outer', outerPos.x, outerPos.z);
  const it = blueprint(
    `${team}-inhib-turret`, team, 'inhibitorTurret', itPos.x, itPos.z, outer.id
  );
  const ih = blueprint(`${team}-inhib`, team, 'inhibitor', ihPos.x, ihPos.z, it.id);
  const ntL = blueprint(
    `${team}-nexus-turret-l`, team, 'nexusTurret', ntLPos.x, ntLPos.z, ih.id
  );
  const ntR = blueprint(
    `${team}-nexus-turret-r`, team, 'nexusTurret', ntRPos.x, ntRPos.z, ih.id
  );
  const nx = blueprint(
    `${team}-nexus`, team, 'nexus', nxPos.x, nxPos.z, [ntL.id, ntR.id]
  );
  return [outer, it, ih, ntL, ntR, nx];
}

export function getAramStructures() {
  return [...teamStructures('blue'), ...teamStructures('red')];
}
